import type { OnboardingFormat, OnboardingLength } from "src/types/onboarding-content";

export type OnboardingState = {
  dismissed: boolean;
  lastTopicId: string | null;
  format: OnboardingFormat | null;
  textLength: OnboardingLength;
};

// Retains dismissal across remounts when a browser denies persistent storage.
const memory = new Map<string, OnboardingState>();
const defaults = (): OnboardingState => ({ dismissed: false, lastTopicId: null, format: null, textLength: "short" });

export const onboardingStorageKey = (userId: number, providerId: number) =>
  `admin-onboarding:v1:${userId}:${providerId}`;

export function readOnboardingState(key: string): OnboardingState {
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const value = JSON.parse(raw);
      if (!value || typeof value !== "object") return memory.get(key) ?? defaults();
      const state: OnboardingState = {
        dismissed: value.dismissed === true,
        lastTopicId: typeof value.lastTopicId === "string" ? value.lastTopicId : null,
        format: ["text", "images", "video"].includes(value.format) ? value.format : null,
        textLength: value.textLength === "detailed" ? "detailed" : "short",
      };
      memory.set(key, state);
      return state;
    }
  } catch { /* Malformed or unavailable storage must not interrupt the dashboard. */ }
  return memory.get(key) ?? defaults();
}

export function writeOnboardingState(key: string, state: OnboardingState) {
  memory.set(key, state);
  try { localStorage.setItem(key, JSON.stringify(state)); }
  catch { /* The in-memory copy remains usable for this session. */ }
}
